/**
 * SSE event reader. Pure utility — no React, no app state.
 *
 * Both the live `/chat` POST stream and the `/chat/runs/:id/stream`
 * reattach stream emit text/event-stream payloads delimited by a blank
 * line (`\n\n` or `\r\n\r\n`). TCP chunks can split an event anywhere,
 * so we buffer across `reader.read()` calls and only yield events once
 * we've seen the full delimiter.
 *
 * Previously inlined in two places (`sendMessage` and the reattach
 * loop) which drifted apart on edge cases — extracted to one async
 * generator so any byte-level fix benefits both call sites.
 */

export interface SseEvent {
  type?: string;
  content?: string;
}

interface ReadSseOptions {
  /** When this becomes true, the generator returns immediately without
   *  yielding further events. */
  stopSignal?: { current: boolean };
  /**
   * Fired every time `reader.read()` returns bytes — i.e. any
   * indication that the server is still alive on the wire, including
   * SSE comment heartbeats (`: ping\n\n`) that the parser would
   * otherwise drop. Idle watchdogs in the caller use this to reset
   * their no-progress timer; without it, the watchdog can't tell
   * "no events yet because the agent is thinking" from "the
   * connection is wedged."
   */
  onProgress?: () => void;
}

/**
 * Yield parsed SSE events from a reader until the stream closes or the
 * stop signal trips. Malformed events (no `data:` line, or non-JSON
 * payload) are silently skipped — callers that need raw fallbacks
 * should detect that themselves before invoking this generator.
 */
export async function* readSseEvents(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  options: ReadSseOptions = {},
): AsyncGenerator<SseEvent> {
  const { stopSignal, onProgress } = options;
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    if (stopSignal?.current) return;
    const { done, value } = await reader.read();
    if (done) break;
    onProgress?.();
    buffer += decoder.decode(value, { stream: true });

    while (true) {
      const sepIdx = _findEventDelimiter(buffer);
      if (sepIdx === -1) break;
      const sepLen = buffer.startsWith("\r\n\r\n", sepIdx) ? 4 : 2;
      const eventText = buffer.slice(0, sepIdx);
      buffer = buffer.slice(sepIdx + sepLen);
      if (eventText.length === 0) continue;
      const parsed = _parseEventBlock(eventText);
      if (parsed) yield parsed;
    }
  }

  // Trailing event without final delimiter — happens when the server
  // closes mid-flush. Try to parse one last time.
  if (buffer.trim()) {
    const parsed = _parseEventBlock(buffer);
    if (parsed) yield parsed;
  }
}

/** Earliest event-end delimiter (`\n\n` or `\r\n\r\n`) in the buffer,
 *  or -1 if none yet. */
function _findEventDelimiter(buffer: string): number {
  const a = buffer.indexOf("\n\n");
  const b = buffer.indexOf("\r\n\r\n");
  if (a === -1) return b;
  if (b === -1) return a;
  return Math.min(a, b);
}

/**
 * Concatenate every `data:` line in one event block (per SSE spec —
 * comment lines starting with `:` are ignored, e.g. our `: ping`
 * heartbeat) and JSON-parse the result. Returns null when the block
 * has no data lines or the payload isn't JSON; the caller decides
 * whether that's recoverable.
 */
function _parseEventBlock(eventText: string): SseEvent | null {
  const dataLines: string[] = [];
  for (const rawLine of eventText.split("\n")) {
    const line = rawLine.endsWith("\r") ? rawLine.slice(0, -1) : rawLine;
    if (line.startsWith("data:")) {
      // Strip the single optional leading space after "data:".
      dataLines.push(line.startsWith("data: ") ? line.slice(6) : line.slice(5));
    }
  }
  if (dataLines.length === 0) return null;
  try {
    return JSON.parse(dataLines.join("\n")) as SseEvent;
  } catch {
    return null;
  }
}
